/**
 * CoutDetailBlock — Palier A' Phase 4
 *
 * Bloc "Analyse cout" affiche dans le modal detail bijou (BijouxAvance).
 * Recupere GET /api/bijoux/<id>/cout-detail et affiche :
 *   A) Cout historique (fige fab) + flag fiabilite
 *   B) Cout remplacement aujourd'hui (recalcule prix or actuel)
 *   C) Prix vente
 *   + 3 marges : reelle comptable, remplacement, stock dormant
 *
 * Style : tokens design Bloc 0 (champagne / encre / ivoire).
 * Lecture seule UI. Pour modifier prix : bouton Modifier en haut du modal.
 */
import React, { useState, useEffect } from 'react';
import {
  TrendingUp, History, RefreshCw, Gem,
  AlertCircle, AlertTriangle, CheckCircle, Minus, HelpCircle,
  WifiOff, Loader2, Info,
} from 'lucide-react';
import { bijouxAPI } from '../../services/api';

const fmtDH = (val) => {
  if (val === null || val === undefined) return '--';
  return new Intl.NumberFormat('fr-MA', {
    style: 'currency',
    currency: 'MAD',
    maximumFractionDigits: 2,
  }).format(val);
};

const FIABILITE_MAP = {
  fiable: {
    Icon: CheckCircle,
    cls:  'bg-emerald-50 text-emerald-700 border-emerald-200',
    label:'Fiable',
  },
  approximation: {
    Icon: AlertTriangle,
    cls:  'bg-amber-50 text-amber-700 border-amber-200',
    label:'Approximation',
  },
  absent: {
    Icon: Minus,
    cls:  'bg-slate-50 text-slate-500 border-slate-200',
    label:'Absent',
  },
  indeterminable: {
    Icon: HelpCircle,
    cls:  'bg-slate-50 text-slate-500 border-slate-200',
    label:'Indeterminable',
  },
};

const ERROR_TITRES = {
  '404':     'Bijou introuvable',
  '503':     'Donnees prix or non disponibles',
  'network': 'Connexion impossible',
  '500':     'Erreur serveur',
};

export default function CoutDetailBlock({ bijouId }) {
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [data,    setData]    = useState(null);

  useEffect(() => {
    if (!bijouId) return;
    let cancelled = false;

    setLoading(true);
    setError(null);

    bijouxAPI.getCoutDetail(bijouId)
      .then(({ data: payload }) => {
        if (!cancelled) setData(payload);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err.response?.status;
        const msg    = err.response?.data?.error || err.message;
        const detail = err.response?.data?.detail;
        if (status === 404)        setError({ type: '404',     message: msg });
        else if (status === 503)   setError({ type: '503',     message: detail || msg });
        else if (!err.response)    setError({ type: 'network', message: msg });
        else                       setError({ type: '500',     message: msg });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [bijouId]);

  // ── Loading skeleton ──────────────────────────────────────
  if (loading) {
    return (
      <section className="rounded-2xl border border-ivoire-300 bg-white p-6 mb-6 shadow-luxe">
        <div className="flex items-center gap-2 mb-4">
          <Loader2 className="h-4 w-4 animate-spin text-champagne-700" />
          <span className="text-xs tracking-luxe-wide uppercase font-medium text-champagne-700">
            Analyse cout
          </span>
        </div>
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 rounded-xl shimmer-gold bg-ivoire-100" />
          ))}
        </div>
        <div className="h-32 rounded-2xl shimmer-gold bg-encre-800/10" />
      </section>
    );
  }

  // ── Erreurs ──────────────────────────────────────────────
  if (error) {
    const ErrorIcon = error.type === 'network' ? WifiOff : AlertCircle;
    const isCritical = error.type === '404' || error.type === '500' || error.type === '503';
    const cls = isCritical
      ? 'bg-red-50 border-red-200 text-red-700'
      : 'bg-slate-50 border-slate-200 text-slate-600';
    const titre = ERROR_TITRES[error.type] || 'Erreur';
    const message =
      error.type === '404'      ? "Ce bijou n'existe pas dans la base de donnees." :
      error.type === '503'      ? "Mettre a jour la table prix_or_actuel. Contacter administrateur." :
      error.type === 'network'  ? "Impossible de charger les donnees cout. Verifier la connexion." :
      error.type === '500'      ? (error.message || "Erreur interne. Reessayer ou contacter administrateur.") :
                                  error.message;

    return (
      <section className={`rounded-2xl border p-5 mb-6 ${cls}`}>
        <div className="flex items-start gap-3">
          <ErrorIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-sm">{titre}</h4>
            <p className="text-xs mt-1 opacity-80">{message}</p>
          </div>
        </div>
      </section>
    );
  }

  if (!data) return null;

  // ── Data nominal ─────────────────────────────────────────
  const { cout_historique, cout_remplacement_actuel, prix_vente, marges } = data;
  const fiabilite     = cout_historique?.fiabilite || 'indeterminable';
  const fiabiliteInfo = FIABILITE_MAP[fiabilite] || FIABILITE_MAP.indeterminable;
  const FiabIcon      = fiabiliteInfo.Icon;
  const isAbsent      = fiabilite === 'absent';
  const valeurA       = isAbsent ? null : cout_historique?.valeur;
  const margeRemp     = marges?.marge_remplacement;
  const margeRempPos  = margeRemp === null || margeRemp === undefined ? true : margeRemp >= 0;

  // Sous-texte marge reelle comptable selon fiabilite
  const margeReelleSubtext =
    isAbsent ? 'Non calculable (cout reel absent)' :
    fiabilite === 'fiable' ? 'Basee sur cout historique fiable' :
    fiabilite === 'approximation' ? 'Basee sur cout historique suspect' :
    'Basee sur cout historique indeterminable';

  return (
    <section className="rounded-2xl border border-ivoire-300 bg-white p-6 mb-6 shadow-luxe animate-fade-up">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp className="h-4 w-4 text-champagne-700" />
        <span className="text-xs tracking-luxe-wide uppercase font-medium text-champagne-700">
          Analyse cout
        </span>
      </div>
      <hr className="luxe-rule mb-6" />

      {/* 3 cartes A / B / C */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {/* A — Historique */}
        <div className="rounded-xl border border-ivoire-300 bg-ivoire-100 p-5">
          <div className="flex items-center gap-2 mb-3">
            <History className="h-4 w-4 text-encre-400" />
            <span className="text-xs tracking-luxe uppercase font-medium text-encre-500">
              A · Cout historique
            </span>
          </div>
          <p className="font-display text-display-sm nums text-encre-800 leading-none">
            {fmtDH(valeurA)}
          </p>
          <div className={`mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs ${fiabiliteInfo.cls}`}>
            <FiabIcon className="h-3 w-3" />
            <span className="font-medium">{fiabiliteInfo.label}</span>
          </div>
          {cout_historique?.alerte && (
            <p className="mt-2 text-xs italic text-encre-400 leading-relaxed">
              {cout_historique.alerte}
            </p>
          )}
          {isAbsent && (
            <p className="mt-2 text-xs italic text-encre-400 leading-relaxed">
              Cout reel non encore calcule pour ce bijou.
            </p>
          )}
        </div>

        {/* B — Remplacement */}
        <div className="rounded-xl border border-ivoire-300 bg-ivoire-100 p-5">
          <div className="flex items-center gap-2 mb-3">
            <RefreshCw className="h-4 w-4 text-encre-400" />
            <span className="text-xs tracking-luxe uppercase font-medium text-encre-500">
              B · Remplacement aujourd'hui
            </span>
          </div>
          <p className="font-display text-display-sm nums text-encre-800 leading-none">
            {fmtDH(cout_remplacement_actuel?.valeur)}
          </p>
          <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
            <CheckCircle className="h-3 w-3" />
            <span className="font-medium">Calcul actuel</span>
          </div>
          <div className="mt-2 text-xs text-encre-400 space-y-0.5 leading-relaxed">
            <div>{cout_remplacement_actuel?.source_or}</div>
            <div>
              MO {fmtDH(cout_remplacement_actuel?.details?.cout_main_oeuvre)}
              <span className="opacity-60"> ({cout_remplacement_actuel?.details?.source_main_oeuvre})</span>
              {' · '}
              Pierres {fmtDH(cout_remplacement_actuel?.details?.cout_pierres)}
            </div>
          </div>
        </div>

        {/* C — Prix vente */}
        <div className="rounded-xl border border-champagne-200 bg-champagne-50 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Gem className="h-4 w-4 text-champagne-700" />
            <span className="text-xs tracking-luxe uppercase font-medium text-champagne-700">
              C · Prix vente
            </span>
          </div>
          <p className="font-display text-display-sm nums text-champagne-700 leading-none">
            {fmtDH(prix_vente)}
          </p>
          <div className="mt-3 text-xs text-encre-400">
            Affiche en boutique
          </div>
        </div>
      </div>

      {/* Marges — card sombre */}
      <div className="rounded-2xl bg-encre-800 text-ivoire-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-3.5 w-3.5 text-champagne-400" />
          <span className="text-xs tracking-luxe-wide uppercase font-medium text-champagne-400">
            Marges
          </span>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Marge reelle comptable */}
          <div>
            <div className="text-xs uppercase tracking-luxe text-ivoire-300 mb-2">
              Marge reelle comptable
            </div>
            <p className="font-display text-display-xs nums leading-none">
              {fmtDH(marges?.marge_reelle_comptable)}
            </p>
            <p className="mt-2 text-xs text-ivoire-300 italic leading-relaxed">
              {margeReelleSubtext}
            </p>
          </div>

          {/* Marge remplacement */}
          <div>
            <div className="text-xs uppercase tracking-luxe text-ivoire-300 mb-2">
              Marge remplacement
            </div>
            <p className={`font-display text-display-xs nums leading-none ${
              margeRempPos ? 'text-champagne-300' : 'text-bordeaux-500'
            }`}>
              {fmtDH(margeRemp)}
            </p>
            <p className="mt-2 text-xs text-ivoire-300 italic leading-relaxed">
              Basee sur cout recalcule aujourd'hui · toujours fiable
            </p>
          </div>

          {/* Stock dormant */}
          <div>
            <div className="text-xs uppercase tracking-luxe text-ivoire-300 mb-2">
              Stock dormant
            </div>
            <p className="font-display text-display-xs nums leading-none text-ivoire-100">
              {fmtDH(marges?.ecart_stock_dormant)}
            </p>
            <p className="mt-2 text-xs text-ivoire-300 italic leading-relaxed">
              Or apprecie depuis fabrication
              {isAbsent && ' · non calculable'}
            </p>
          </div>
        </div>
      </div>

      {/* Footer mention */}
      <div className="mt-4 flex items-start gap-2 text-xs text-encre-400 italic">
        <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
        <p>
          Affichage uniquement. Pour modifier le prix, utiliser le bouton{' '}
          <strong className="not-italic text-encre-500">Modifier</strong> en haut du modal.
        </p>
      </div>
    </section>
  );
}
